import React, { useState, useEffect, useCallback, useContext } from "react";
import { View, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip,
  Legend as RechartsLegend, ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import "./Leads.css";
import Permiso from "../../router/Permiso";
import { LeadService } from "../../services/LeadService";
import 'react-circular-progressbar/dist/styles.css';
import Input from "../../components/Input";
import RadialSeparators from "../../components/RadialSeparators";
import Select from "../../components/Select";
import Modal from "../../components/Modal";
import Dropzone from "../../components/Dropzone";
import api from '../../config/axiosConfig';
import Preloader from "../../components/Preloader";
import { toast } from "react-toastify";
import { TypesService } from "../../services/TypesService";
import { AuthContext } from "../../context/AuthContext";

const Leads = ({  refreshTrigger = 0 }) => {
  const { token } = useContext(AuthContext);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [searchByName, setSearchByName] = useState("");
  const [state, setState] = useState("");
  const [error, setError] = useState("");
  const [errorModal, setErrorModal] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [productivityList, setProductivityList] = useState([]);
  const [percentStatistic, setPercentStatistic] = useState(0);
  const [modalImport, setModalImport] = useState(false);
  const [file, setFile] = useState();
  const [statusList, setStatusList] = useState([]);
  const navigate = useNavigate();
  const COLORS = ['#22b04c', '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f', '#edc948', '#b07aa1'];


  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await TypesService.get({
          token: token,
          table: 'estado_leads'
        });
        const formattedStatus = response.map(r => ({
          id: r.id,
          nombre: r.descripcion
        }));
        setStatusList(formattedStatus);
      } catch (err) {
        console.error("Error cargando estados:", err);
      }
    };

    fetchStatuses();
  }, [token]);

  const getStatistics =useCallback(async () => {
    const params = {
      token,
      fechaInicio: fechaInicio && fechaInicio.trim() !== "" ? fechaInicio : null,
      fechaFin: fechaFin && fechaFin.trim() !== "" ? fechaFin : null
    };
    const result = await LeadService.getStatistics(params);
    const percent = result.total_leads > 0 ? Math.round((result.conSeguimiento / result.total_leads) * 100) : 0;
    setPercentStatistic(percent);
    const productivityList = result.detallePorUsuario.map(u =>
      ({ name: u.nombre, cantidad: u.cliente_con_seguimiento }));
    setProductivityList(productivityList)
  }, [token, fechaInicio, fechaFin]);

  const exportarExcel = async () => {
    try {
      const filtros = {
        desde: fechaInicio,
        hasta: fechaFin,
        searchByName: searchByName,
        lead: true,
        state: state,
      };

      const blob = await LeadService.exportar(token, filtros);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "listado_leads.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
    }
  };

  const getLeads = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { leads, totalPaginas, totalRegistros } = await LeadService.get({
        token,
        pagina: currentPage,
        pageSize: 20,
        desde: fechaInicio,
        hasta: fechaFin,
        searchByName: searchByName,
        state: state,
      });

      setLeads(leads);
      setTotalPages(totalPaginas);
      setTotalRegistros(totalRegistros);
    } catch (err) {
      setError("Error al cargar las ventas");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, fechaInicio, fechaFin, searchByName, state]);

  useEffect(() => {
      const loadData = async () => {
        await Promise.all([getLeads(), getStatistics()]);
      };
      loadData();
    }, [getLeads, getStatistics, refreshTrigger]);

  return (
    <div className="ventas-admin-content">
      <div className="ventas-admin-tab-content">

        <div className="flex w-full">
          <div className="w-20">
            <div className="ventas-admin-subtitle">
              Leads
            </div>

          </div>
          <div className="w-20">
            <div className="ventas-admin-subtitle pb-10">
              Avance de Gestión
            </div>
            <CircularProgressbarWithChildren
              value={percentStatistic}
              text={`${percentStatistic}%`}
              strokeWidth={10}
              styles={buildStyles({
                strokeLinecap: "butt",
                pathColor: "#8a6fcb",
                textColor: "black"
              })}
            >
              <RadialSeparators
                count={12}
                style={{
                  background: "#fff",
                  width: "2px",
                  height: `${10}%`
                }}
              />
            </CircularProgressbarWithChildren>
          </div>
          <div className="w-40">
            <div className="ventas-admin-subtitle pb-10">
              Productividad
            </div>
            {
              productivityList.length > 0 && (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={productivityList}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <RechartsLegend />
                    <Bar dataKey="cantidad" name="Leads con Seguimiento">
                      {productivityList.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                      <LabelList dataKey="cantidad" position="top" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )
            }
          </div>
        </div>

        <div className="ventas-table-header">
          <h2 className="ventas-table-title">Lista de potenciales clientes </h2>
          <div className="productos-actions">
            {/* <button onClick={() => {
                
              }} className="btn-filtrar">
                + Nuevo
              </button> */}
            {/* <Permiso permiso="import_lead">
              <button onClick={() => setModalImport(true)} className="btn-filtrar" style={{ backgroundColor: '#f56100' }}>
                Importar
              </button>
            </Permiso> */}
            <Permiso permiso="export_lead">
              <button onClick={exportarExcel} className="btn-exportar">
                Exportar a Excel
              </button>
            </Permiso>
          </div>
        </div>

        <div className="usuarios-filtros">
          <Input title="Buscar por nombre o número documento" value={searchByName} setValue={setSearchByName} placeholder="Ingrese el nombre del cliente o número" className="filtro-item" />
          <Select
            title="Estado leads"
            value={state}
            setValue={setState}
            options={[
              { id: "", nombre: "Todos los estados" },
              ...statusList
            ]}
            className="filtro-item"
          />
          <div className="filtro-item">
            <label htmlFor="fecha-inicio">Fecha Inicio:</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
          </div>
          <div className="filtro-item">
            <label htmlFor="fecha-fin">Fecha Fin:</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
          </div>
          <div className="filtro-item">
            <button onClick={() => {
              if (state !== "" || searchByName !== "" || fechaInicio !== "" || fechaFin !== "") {
                getLeads();
              }
            }} className="btn-filtrar">
              Buscar
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">Cargando ventas...</div>
        ) : (
          <>
            <div className="ventas-table-wrapper">
              <table className="ventas-table">
                <thead>
                  <tr>
                    <th>Tipo de Documento</th>
                    <th>Número</th>
                    <th>Cliente</th>
                    <th>F. Precliente</th>
                    <th>Estado</th>
                    <th>Comentarios</th>
                    <th># Contac</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, index) => {
                    return (
                      <tr key={lead.id}>
                        <td> {lead.tipo_documento}</td>
                        <td>{lead.numero_documento}</td>
                        <td>
                          {lead.nombres} {lead.ap_paterno} {lead.ap_materno}
                        </td>


                        <td>{new Date(lead.fecha_precliente).toLocaleDateString()}</td>
                        <td>
                          {lead.estado}
                        </td>
                        <td>{lead.observacion}</td>
                        <td>
                          {lead.numero_correlativo}
                        </td>
                        <td className="ventas-acciones">
                          <Permiso permiso="leads_view">
                            <span
                              style={{ cursor: "pointer" }}
                              onClick={() => navigate(`/admin/leads/registrar/${lead.id}/only`)}
                            >
                              <View size={20} />
                            </span>
                          </Permiso>
                          <Permiso permiso="add_seguimiento_lead">
                            {!lead.codigo_venta_cliente && (
                              <span
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  navigate(`/admin/leads/registrar/${lead.id}`);
                                }}
                              >
                                <Phone size={20} />
                              </span>
                            )}
                          </Permiso>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <span style={{ marginLeft: "12px", fontSize: "14px", color: "#555" }}>
              {" "}
              Total de registros: {totalRegistros}{" "}
            </span>
            {/* Paginación */}
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Anterior
              </button>
              <span>
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Siguiente
              </button>
            </div>
          </>
        )}
      </div>
      <Modal
        show={modalImport}
        title={"Arrastra o selecciona el archivo"}
        onClickButtonAction={async () => {
          if (file) {
            setErrorModal('');

            const formData = new FormData();
            formData.append("file", file);

            try {
              const response = await api.post("/eads/importar",
                formData,
                {
                  headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                  },
                }
              );

              const { success, resultados } = response.data;

              if (success) {
                const errores = resultados.filter((r) => r.accion === "error");
                if (resultados.length > 0) {
                  if (errores.length > 0) {
                    toast.error(
                      <div>
                        <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                          Errores en la importación
                        </div>
                        <div style={{ fontSize: "14px" }}>
                          {errores.map((e, index) => (
                            <div key={e.documento}>
                              Codigo: {e.documento} — Motivo: {e.motivo}
                            </div>
                          ))}
                        </div>
                      </div>,
                      {
                        position: "top-center",
                        autoClose: false,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "colored",
                      }
                    );
                  }
                } else {
                  toast.success("Leads Importados Exitosamente", {
                    position: "top-center",
                    autoClose: false,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored",
                  });
                  getLeads();
                }
              }
              setLoading(false);
              setFile(null);
              setModalImport(false);
            } catch (error) {
              setLoading(false);
              const mensajeError =
                error.response?.data?.error || "Error inesperado al importar";
              toast.error(` ${mensajeError}`);
            }

          }
          else {
            setErrorModal("Seleccione un archivo");
          }
        }}
        textButtonAction={"Importar"}
        onClose={() => setModalImport(false)}
        content={<>
          <div style={{ marginBottom: 10 }}>
            <span style={{ marginTop: "20px", textAlign: "center" }}>
              <a
                href="/formatos/import_leads.xlsx"
                download
                style={{
                  textDecoration: "none",
                  color: "#333",
                  fontWeight: "500",
                  fontSize: "14px",
                  display: "inline-block",
                }}
              >
                Descargar formato de ejemplo para importar
              </a>
            </span>
          </div>
          <Dropzone onFileSelect={(file) => {
            setFile(file);
          }} />

          {loading && <Preloader />}
          {errorModal && <div className="error-message">{errorModal}</div>}
        </>}
      />
    </div>
  );
};

Leads.propTypes = {
  refreshTrigger: PropTypes.number
}

export default Leads;
